import {isNil, uniqBy} from 'ramda';

import {ApiEnumKey, GetAllApiEnumsApiResponse} from '@dms/api/shared';

export type PropertyName =
  | 'car_style'
  | 'van_style'
  | 'dimensions_length'
  | 'dimensions_height'
  | 'fuel_type'
  | 'drive'
  | 'feature'
  | 'emission_standard'
  | 'service_book_type'
  | 'battery_ownership_type'
  | 'battery_type'
  | 'body_color'
  | 'carvago_feature'
  | 'charging_connector_type'
  | 'color_type'
  | 'condition'
  | 'cost'
  | 'country'
  | 'currency'
  | 'door_count'
  | 'emission_class'
  | 'feature_category'
  | 'fuel_unit'
  | 'hand_drive_type'
  | 'hybrid_type'
  | 'interior_color'
  | 'interior_material'
  | 'seller_type'
  | 'service_book_state'
  | 'tire_aspect_ratio'
  | 'tire_make'
  | 'tire_model'
  | 'tire_rim_diameter'
  | 'tire_season'
  | 'tire_width'
  | 'transmission'
  | 'vehicle_type';

/**
 * Properties the vehicle catalogue publishes only for `VEHICLETYPE_VAN`.
 * Everything else is read from the passenger car set, the way sourcing has always shown it.
 */
const VAN_ONLY_PROPERTY_NAMES: PropertyName[] = [
  'van_style',
  'dimensions_length',
  'dimensions_height',
];

/**
 * Enums are requested for several vehicle types at once, and most properties (`fuel_type`,
 * `transmission`, `body_color`, ...) come back once per type. Merging those would leak van-only
 * keys into every dropdown, so each property is read from the vehicle type it belongs to.
 */
const isRelevantVehicleType = (
  propertyName: PropertyName,
  vehicleType: GetAllApiEnumsApiResponse[number]['vehicle_type']
): boolean =>
  VAN_ONLY_PROPERTY_NAMES.includes(propertyName)
    ? vehicleType === 'VEHICLETYPE_VAN'
    : vehicleType === 'VEHICLETYPE_PASSENGER_CAR' || isNil(vehicleType);

export const getEnumByProperty = (
  propertyName: PropertyName,
  enums?: GetAllApiEnumsApiResponse
): ApiEnumKey[] =>
  // A property can still appear more than once within one vehicle type, so deduplicate by key
  uniqBy(
    (key) => key.const_key,
    enums
      ?.filter(
        (item) =>
          item.property_name === propertyName &&
          isRelevantVehicleType(propertyName, item.vehicle_type)
      )
      .flatMap((item) => item.keys) ?? []
  );
